import { useEffect, useMemo, useState } from 'react'
import { useController, UseFormReturn } from 'react-hook-form'
import clsx from 'clsx'

import { TableFormRtnInput } from './TableFormRtnInput'
import { useCombinedLogsTableFormStateStore } from '@/modules/CombinedLogs/store'
import { TableFormTripToInput } from './TableFormTripToInput'
import { useGetTrips } from '@/modules/CombinedLogs/api/useGetTrips'
import { SuggestionInputTable } from '@/ui/SuggestionInputTable'
import { InputFormValues } from '@/modules/CombinedLogs/types'

import styles from './TableFormTripInput.module.scss'

interface Props {
    form: UseFormReturn<InputFormValues>
}

const MAX_LENGTH = 4

export const TableFormTripInput = ({ form }: Props) => {
    const { field: tripField } = useController({ control: form.control, name: 'trip' })
    const { setTrip, selectedTrips, setSelectedTrips, isBulkCurtailment } = useCombinedLogsTableFormStateStore()
    const [isTripFocused, setIsTripFocused] = useState(false)
    const [isRtnFocused, setIsRtnFocused] = useState(false)
    const [isTripToFocused, setIsTripToFocused] = useState(false)

    const { data: trips } = useGetTrips()

    const trip = form.watch('trip')
    const rtnTrips = form.watch('rtnTrips')
    const bulkCurtailmentTripTo = form.watch('bulkCurtailmentTripTo')

    const displayedValue = useMemo(
        () => (!isBulkCurtailment ? `${trip}/${rtnTrips}` : `${trip}-${bulkCurtailmentTripTo}`),
        [isBulkCurtailment, trip, rtnTrips, bulkCurtailmentTripTo]
    )

    const placeholder = useMemo(() => (isBulkCurtailment ? 'From' : 'Trip'), [isBulkCurtailment])

    const isSecondTripInputHidden = useMemo(
        () => (!isBulkCurtailment ? !isRtnFocused && !!rtnTrips : !isTripToFocused && !!bulkCurtailmentTripTo),
        [isBulkCurtailment, isRtnFocused, rtnTrips, isTripToFocused, bulkCurtailmentTripTo]
    )

    const options = useMemo(() => {
        const allTrips = trips ? [...trips.inbound, ...trips.outbound] : []

        return allTrips
            .map((item) => ({ id: item.id, label: item.trip.toString(), value: item.trip.toString() }))
            .sort((tripA, tripB) => +tripA.value - +tripB.value)
    }, [trips])

    // useEffect 1: Single trip auto-fill
    // When the trip input loses focus and a trip value is set, look up the matching
    // trip object and auto-fill depotCode, run, duty, and fleet from its data.
    useEffect(() => {
        if (isTripFocused || !trip) {
            return
        }

        const allTrips = trips
            ? [...trips.inbound, ...trips.outbound].sort((itemA, itemB) => +itemA.trip - +itemB.trip)
            : []

        const tripObject = allTrips.find((item) => item.trip.toString() === trip)

        if (!tripObject) {
            return
        }

        form.setValue('depotCode', tripObject.depotCode?.toString())
        form.setValue('run', tripObject.run?.toString())

        // Only populate duty if there's a single duty for the trip
        if (tripObject.duty && tripObject.duty.length === 1) {
            form.setValue('duty', tripObject.duty[0].toString())
        } else {
            form.setValue('duty', '')
        }

        form.setValue('fleet', tripObject.fleetNumber?.toString())
    }, [isTripFocused, trip])

    // useEffect 2: Return trips auto-fill (non-bulk curtailment mode)
    // When the return trip input loses focus and there are selected trips, auto-fill
    // depotCode, run, duty, and fleet from the OUTBOUND trip.
    useEffect(() => {
        if (isRtnFocused || !trip || !selectedTrips.length || isBulkCurtailment) {
            return
        }

        const allTrips = trips
            ? [...trips.inbound, ...trips.outbound].sort((itemA, itemB) => +itemA.trip - +itemB.trip)
            : []
        const outboundTrip = allTrips.find((item) => item.trip.toString() === trip)

        form.setValue('depotCode', outboundTrip?.depotCode?.toString())
        form.setValue('run', outboundTrip?.run?.toString())
        if (outboundTrip?.duty && outboundTrip.duty.length === 1) {
            form.setValue('duty', outboundTrip.duty[0].toString())
        } else {
            form.setValue('duty', '')
        }
        form.setValue('fleet', outboundTrip?.fleetNumber?.toString())
    }, [isRtnFocused])

    // useEffect 3: Bulk curtailment range auto-fill
    // When in bulk curtailment mode and both the "from" and "to" trips are set,
    // find all trips in that range, store them as selectedTrips, and auto-fill
    // two sets of form fields: the "from" trip's depotCode/run/duty/fleet and
    // the "to" trip's bulkCurtailmentRunTo/DutyTo/FleetTo.
    useEffect(() => {
        if (!trip || !bulkCurtailmentTripTo || !isBulkCurtailment) {
            return
        }

        const allTrips = trips
            ? [...trips.inbound, ...trips.outbound].sort((itemA, itemB) => +itemA.trip - +itemB.trip)
            : []
        const tripIndex = allTrips.findIndex((item) => item.trip.toString() === trip)
        const tripToIndex = allTrips.findIndex((item) => item.trip.toString() === bulkCurtailmentTripTo)

        const tripObject = allTrips[tripIndex]
        const tripToObject = allTrips[tripToIndex]

        setSelectedTrips(allTrips.slice(tripIndex, tripToIndex + 1))

        if (tripObject && tripToObject) {
            form.setValue('depotCode', tripObject.depotCode?.toString())
            form.setValue('run', tripObject.run?.toString())
            if (tripObject.duty && tripObject.duty.length === 1) {
                form.setValue('duty', tripObject.duty[0].toString())
            } else {
                form.setValue('duty', '')
            }
            form.setValue('fleet', tripObject.fleetNumber?.toString())
            form.setValue('bulkCurtailmentRunTo', tripToObject.run?.toString())
            if (tripToObject.duty && tripToObject.duty.length === 1) {
                form.setValue('bulkCurtailmentDutyTo', tripToObject.duty[0].toString())
            } else {
                form.setValue('bulkCurtailmentDutyTo', '')
            }
            form.setValue('bulkCurtailmentFleetTo', tripToObject.fleetNumber?.toString())
        }
    }, [bulkCurtailmentTripTo, trip])

    return (
        <>
            <div
                onClick={(e) => {
                    e.preventDefault()
                    if (isBulkCurtailment) {
                        setIsTripToFocused(true)
                    } else {
                        setIsRtnFocused(true)
                    }
                }}
                className={clsx(styles.resultTripLabel, {
                    [styles.hidden]: !isBulkCurtailment
                        ? isRtnFocused || !trip || !rtnTrips
                        : !bulkCurtailmentTripTo || !trip || isTripToFocused,
                })}
            >
                {displayedValue}
            </div>
            <div
                className={clsx(styles.tripInputContainer, {
                    [styles.hidden]: !!trip && isSecondTripInputHidden,
                })}
            >
                <SuggestionInputTable
                    value={tripField.value}
                    options={options}
                    variant="table"
                    type="text"
                    transparent
                    fullWidth
                    pattern="\d*"
                    inputMode="numeric"
                    placeholder={placeholder}
                    className={styles.tripPopup}
                    inputClassName={styles.tripInput}
                    displayValueInsteadOfLabel
                    onlyFromDropdown={isBulkCurtailment}
                    maxLength={MAX_LENGTH}
                    canTabThrough
                    onChange={(value) => {
                        const onlyDigits = value.replace(/\D/g, '')
                        tripField.onChange(onlyDigits)
                        setTrip(onlyDigits)
                    }}
                    onFocus={() => setIsTripFocused(true)}
                    onBlur={() => setIsTripFocused(false)}
                />
                <div className={clsx({ [styles.hidden]: !isBulkCurtailment ? !trip && !isTripFocused : false })}>
                    {isBulkCurtailment ? (
                        <TableFormTripToInput form={form} setFocused={setIsTripToFocused} />
                    ) : (
                        <TableFormRtnInput
                            name="rtnTrips"
                            control={form.control}
                            setFocused={setIsRtnFocused}
                            selectedTrips={selectedTrips}
                            setSelectedTrips={setSelectedTrips}
                            hidden={!trip}
                            trip={trip}
                            isBulkCurtailment={isBulkCurtailment}
                        />
                    )}
                </div>
            </div>
        </>
    )
}
